import { Colors } from "@/enums/Colors";
import { useLanguage } from "@/hooks/useLanguage";
import { AVAILABLE_LANGUAGES } from "@/i18n/languages";
import { LanguageOutlined } from "@mui/icons-material";
import { Fab, Menu, MenuItem, Typography, styled } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface LanguageSelectorProps {
  position?: {
    bottom?: string | number;
    right?: string | number;
    top?: string | number;
    left?: string | number;
  };
}

export default function LanguageSelector({ position = { bottom: 20, right: 20 } }: LanguageSelectorProps) {
  const { t, i18n } = useTranslation("common");
  const { language, setLanguage } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode: string) => {
    setLanguage(languageCode);
    i18n.changeLanguage(languageCode);
    handleClose();
  };

  return (
    <>
      <FloatingFab
        color="primary"
        size="medium"
        onClick={handleClick}
        position={position}
        aria-label={t("languageSelector.selectLanguage")}
      >
        <LanguageOutlined />
      </FloatingFab>

      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {AVAILABLE_LANGUAGES.map((lang) => (
          <StyledMenuItem key={lang.locale} onClick={() => handleLanguageChange(lang.locale)} selected={lang.locale === language}>
            <Typography>{lang.label}</Typography>
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </>
  );
}

const FloatingFab = styled(Fab)<{ position: any }>(({ position }) => ({
  position: "fixed",
  zIndex: 1200,
  backgroundColor: Colors.LAZO_PURPLE,
  color: "white",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  "&:hover": {
    backgroundColor: Colors.PURPLE,
  },
  ...position,
}));

const StyledMenu = styled(Menu)({
  "& .MuiPaper-root": {
    borderRadius: "8px",
    minWidth: "140px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  },
});

const StyledMenuItem = styled(MenuItem)<{ selected: boolean }>(({ selected }) => ({
  padding: "8px 16px",
  borderRadius: "4px",
  margin: "2px",
  backgroundColor: selected ? Colors.LAZO_LIGHT_PURPLE : "transparent",
  "&:hover": {
    backgroundColor: selected ? Colors.LAZO_LIGHT_PURPLE : Colors.LIGHT_GRAY_F4F4F4,
  },
}));
